import {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useQuery} from '@apollo/client';
import {Spin, Tooltip} from 'antd';
import {Button} from '@dabafinance/react-components';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {
  GET_EXCHANGE_SUMMARY,
  buildVenueBreakdownQuery,
  readVenueBreakdown,
} from './resolvers';
import {INSTRUMENT_TYPES} from './useExchangeScope';
import styles from './exchanges.module.scss';

const CHEAP = {
  includeSectors: false,
  includeCompositeIndex: false,
  moversLimit: 0,
};

/**
 * Multi Asset → Exchanges.
 *
 * Exchange definition, which until now lived inside Trading View → Orders as a
 * tab literally declared `{key: 'STOCKS', label: 'Exchanges'}` — exchange
 * identity sitting inside an order-management screen.
 *
 * The counts here are enabled instruments only, which is what `getExchanges`
 * reports; the per-type breakdown makes the shape of each exchange legible
 * without opening it.
 */
export const ExchangeList = () => {
  const navigate = useNavigate();
  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const {data, loading} = useQuery(GET_EXCHANGE_SUMMARY, {
    variables: {input: CHEAP},
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const venues = (data?.getExchanges?.data ?? []).filter(node => node.exchange);
  const ids = useMemo(
    () => venues.map(node => node.exchange.id),
    [venues.map(node => node.exchange.id).join('|')],
  );

  const breakdownQuery = useMemo(
    () => buildVenueBreakdownQuery(ids, INSTRUMENT_TYPES),
    [ids.join('|')],
  );

  const {data: breakdownData} = useQuery(breakdownQuery, {
    skip: !ids.length,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const breakdown = useMemo(
    () =>
      breakdownData
        ? readVenueBreakdown(breakdownData, ids, INSTRUMENT_TYPES)
        : null,
    [breakdownData, ids.join('|')],
  );

  return (
    <div>
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <h1 className={shell.title}>Exchanges</h1>
            <div className={shell.sub}>
              The exchanges instruments list on. Identity and narrative content
              live here; order behaviour lives in Trading View.
            </div>
          </div>
          {/* Create used to be reachable only from the Exchanges tab inside
              Stock Listings. That tab is gone, so it lives here now — the
              create screen itself is unchanged. */}
          <div className={shell.headActions}>
            <Button
              label="Create exchange"
              disabled={!pageOperations?.create}
              onClick={() => navigate('/dashboard/stocks/create-stock')}
            />
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="query-loading">
          <Spin size="large" />
          <h1>Loading…</h1>
        </div>
      ) : (
        <div className={styles.venueGrid}>
          {venues.map(node => {
            const {exchange, instrumentCount} = node;
            const counts = breakdown?.[exchange.id];
            return (
              <div
                key={exchange.id}
                role="button"
                tabIndex={0}
                className={styles.venueCard}
                onClick={() => navigate(`/dashboard/exchanges/${exchange.id}`)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/dashboard/exchanges/${exchange.id}`);
                  }
                }}>
                <div className={styles.venueHead}>
                  <div>
                    <div className={styles.venueCode}>{exchange.code}</div>
                    <div className={styles.venueName}>
                      {exchange.stockExchangeName}
                    </div>
                  </div>
                  {exchange.logoImage && (
                    <img
                      className={styles.venueLogo}
                      src={exchange.logoImage}
                      alt=""
                    />
                  )}
                </div>

                <div className={styles.breakdown}>
                  {INSTRUMENT_TYPES.map(type => (
                    <div className={styles.breakdownRow} key={type.key}>
                      <span className={styles.breakdownLabel}>
                        {type.label}
                      </span>
                      <span className={styles.breakdownValue}>
                        {counts ? counts[type.instrumentKey] : '·'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className={styles.venueFoot}>
                  <Tooltip title="Enabled instruments across every type">
                    <span className={styles.flag}>
                      {instrumentCount?.toLocaleString() ?? 0} live
                    </span>
                  </Tooltip>
                  <span
                    className={`${styles.flag} ${
                      exchange.tradeable ? styles.flagOn : styles.flagOff
                    }`}>
                    {exchange.tradeable ? 'Tradeable' : 'Not tradeable'}
                  </span>
                  {/* Missing here means the exchange cannot be saved at all —
                      `baseCurrencyCode` is NON_NULL on the update input. */}
                  {exchange.baseCurrencyCode ? (
                    <span className={styles.flag}>
                      {exchange.baseCurrencyCode}
                    </span>
                  ) : (
                    <Tooltip title="No base currency set. The update mutation requires one, so this exchange cannot be saved until it is fixed.">
                      <span className={`${styles.flag} ${styles.flagOff}`}>
                        No currency
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!pageOperations?.view && !loading && (
        <div className={styles.empty}>
          You do not have permission to view exchanges.
        </div>
      )}
    </div>
  );
};

export default ExchangeList;
